import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TabSdk } from '@techextensor/tab-sdk';
import { TabGetService } from '@techextensor/tab-core-utility';
import { OrgAppInfoHelperService } from '../services/org-app-info-helper.service';
import { Constants } from '../const/constants';
import { environment } from '../../../environments/environment';

/**
 * Loads org/app info and resolves the app environment once per entry into the
 * `documents` section (attached to canActivate on the PARENT route only, not
 * canActivateChild) — so it runs exactly once regardless of how many times the
 * user navigates between the document list ('') and a specific document (':id'),
 * which are sibling route configs pointing at the same component and would
 * otherwise destroy/recreate DocumentComponent (and re-run this work) on every
 * such navigation.
 *
 * CrossTabAuthenticationService.initialize() deliberately skips loading org/app
 * info for '/documents' paths, and guestGuard doesn't load it as a fallback
 * either — so on a fresh tab (empty per-tab session storage, even though
 * localStorage/tokenInfo may already exist from another tab), TabSdk.store.appId
 * is null without this guard. Uses the lightweight loaders (loadOrgInfo/loadAppInfo),
 * NOT the full processAppInfo()/loadApp() path used for full app bootstrap.
 */
export const documentsContextGuard: CanActivateFn = async () => {
  const orgAppInfoHelper = inject(OrgAppInfoHelperService);
  const tabGetService = inject(TabGetService);

  if (!TabSdk.store.getOrgInfo()?.Id) {
    await orgAppInfoHelper.loadOrgInfo();
  }
  if (!TabSdk.store.getAppInfo()?.Id) {
    await orgAppInfoHelper.loadAppInfo();
  }

  let appEnv = {
    Id: environment.id,
    Name: environment.name
  };

  if (environment.commonConfig.applicationId?.toLowerCase() !== TabSdk.store.appId?.toLowerCase()) {
    const response: any = await firstValueFrom(
      tabGetService.executeRAWDSQWithName({
        AppObjectName: Constants.TABMD_Environment,
        DSQName: Constants.Get_App_SortedEnvironments,
        Reqtokens: { AppId: TabSdk.store.appId },
      })
    );

    if (response?.IsSuccess && response?.Result?.length > 0) {
      // Always pick the highest-Sequence environment (e.g. QA over Development)
      appEnv = response.Result.sort((a: any, b: any) => b?.Sequence - a?.Sequence)[0] || appEnv;
    }
  }

  TabSdk.store.environmentId = appEnv?.Id;
  TabSdk.store.environmentName = appEnv?.Name;

  return true;
};
